
import { CertificateMetadata, CertificationStatus } from '@/types/certification';

interface CertificateHistoryEntry {
  timestamp: string;
  action: 'issuance' | 'status_change' | 'verification' | 'revision' | 'renewal';
  description: string;
  performedBy?: string;
  details?: Record<string, any>;
}

export interface CertificateHistoryRecord {
  certificateId: string;
  entries: CertificateHistoryEntry[];
}

// In-memory storage for certificate history (in a real app, this would be in a database)
const certificateHistoryStore: Record<string, CertificateHistoryRecord> = {};

export const trackCertificateIssuance = (
  certificate: CertificateMetadata,
  issuedBy: string
) => {
  const historyEntry: CertificateHistoryEntry = {
    timestamp: new Date().toISOString(),
    action: 'issuance',
    description: `Certificate issued for ${certificate.programTitle}`,
    performedBy: issuedBy,
    details: {
      level: certificate.certificationLevel,
      verificationCode: certificate.verificationCode,
    }
  };

  initializeHistory(certificate.id, historyEntry);
  return historyEntry;
};

export const trackStatusChange = (
  certificateId: string,
  previousStatus: CertificationStatus,
  newStatus: CertificationStatus,
  changedBy: string,
  notes?: string
) => {
  const historyEntry: CertificateHistoryEntry = {
    timestamp: new Date().toISOString(),
    action: 'status_change',
    description: `Status changed from ${previousStatus} to ${newStatus}`,
    performedBy: changedBy,
    details: {
      previousStatus,
      newStatus,
      notes
    }
  };

  addHistoryEntry(certificateId, historyEntry);
  return historyEntry;
};

export const trackVerificationAttempt = (
  certificateId: string,
  success: boolean,
  verifiedBy: string,
  details?: { ipAddress?: string; location?: string }
) => {
  const historyEntry: CertificateHistoryEntry = {
    timestamp: new Date().toISOString(),
    action: 'verification',
    description: `Verification attempt: ${success ? 'successful' : 'failed'}`,
    performedBy: verifiedBy,
    details: {
      success,
      ...details
    }
  };

  addHistoryEntry(certificateId, historyEntry);
  return historyEntry;
};

export const trackCertificateRevision = (
  certificateId: string,
  revisionReason: string,
  revisedBy: string,
  changes: Record<string, any>
) => {
  const historyEntry: CertificateHistoryEntry = {
    timestamp: new Date().toISOString(),
    action: 'revision',
    description: `Certificate revised: ${revisionReason}`,
    performedBy: revisedBy,
    details: changes
  };

  addHistoryEntry(certificateId, historyEntry);
  return historyEntry;
};

export const trackCertificateRenewal = (
  certificateId: string,
  renewedBy: string,
  newExpiryDate: string
) => {
  const historyEntry: CertificateHistoryEntry = {
    timestamp: new Date().toISOString(),
    action: 'renewal',
    description: 'Certificate renewed',
    performedBy: renewedBy,
    details: {
      newExpiryDate
    }
  };

  addHistoryEntry(certificateId, historyEntry);
  return historyEntry;
};

export const getCertificateHistory = (certificateId: string): CertificateHistoryEntry[] => {
  return certificateHistoryStore[certificateId]?.entries || [];
};

// Helper functions
const initializeHistory = (certificateId: string, initialEntry: CertificateHistoryEntry) => {
  certificateHistoryStore[certificateId] = {
    certificateId,
    entries: [initialEntry]
  };
};

const addHistoryEntry = (certificateId: string, entry: CertificateHistoryEntry) => {
  if (!certificateHistoryStore[certificateId]) {
    initializeHistory(certificateId, entry);
    return;
  }

  certificateHistoryStore[certificateId].entries.push(entry);
};
